import { getEnv } from './getEnv'

/** Logo URL types for different image services */
export type LogoUrlType =
  | 'prechecked-provider'
  | 'point-solutions'
  | 'employer-logo'

/**
 * Takes an iconImage and returns the full url for the image based on the
 * current environment and type. URL formats provided by the BE:
 *
 * Prechecked Provider:
 *
 * - DEV: https://dev-prechecked-provider-image.sidecarhealth.com/{iconImage}
 * - QA: https://qa-prechecked-provider-image.sidecarhealth.com/{iconImage}
 * - PROD: https://prechecked-provider-image.sidecarhealth.com/{iconImage}
 *
 * Point Solutions:
 *
 * - DEV: https://dev-point-solution-image.sidecarhealth.com/{iconImage}
 * - QA: https://qa-point-solution-image.sidecarhealth.com/{iconImage}
 * - PROD: https://point-solution-image.sidecarhealth.com/{iconImage}
 *
 * Employer Logo:
 *
 * - DEV: https://dev-employer-group-image.sidecarhealth.com/{iconImage}
 * - QA: https://qa-employer-group-image.sidecarhealth.com/{iconImage}
 * - PROD: https://employer-group-image.sidecarhealth.com/{iconImage}
 *
 * @param iconImage - The icon image name/path to be appended to the URL
 * @param type - The type of logo service to use
 * @returns The complete URL for the logo
 */
export const getProviderLogoUrl = (
  iconImage: string = '',
  type: LogoUrlType = 'prechecked-provider',
): string => {
  // Some sources (e.g. ad-serving) already provide a fully-qualified URL.
  // Return it untouched so it isn't re-prefixed with an env/bucket host.
  if (/^https?:\/\//i.test(iconImage)) {
    return iconImage
  }

  // Pass the hostname so the env resolves correctly on the client
  const env = getEnv(
    typeof window !== 'undefined' ? window.location?.hostname : '',
  )
  let environment = ''
  switch (env) {
    case 'prod':
      environment = ''
      break
    case 'qa':
      environment = 'qa-'
      break
    case 'dev':
      environment = 'dev-'
      break
    default:
      environment = 'dev-'
      break
  }

  const domainMap: Record<LogoUrlType, string> = {
    'prechecked-provider': 'prechecked-provider-image',
    'point-solutions': 'point-solution-image',
    'employer-logo': 'employer-group-image',
  }

  const domain = domainMap[type]
  return `https://${environment}${domain}.sidecarhealth.com/${iconImage}`
}
